/**
 * Low-level HTTP request primitive for tmm-reseller-admin (Option A, H §4.3).
 *
 * The transport is the **vendored fleet axios factory** (`./httpClientFactory`, from
 * `software-architect-ai/templates/api-axios-instance.ts`) configured via `createApiClient`.
 * The four interceptors (base+headers / bearer / csrf / silent-refresh+error-normalize) are
 * centralized there; this module supplies tmm-reseller-admin's config and a THIN wrapper.
 * Interceptor changes come from RE-VENDORING the fleet template, never from editing it locally.
 *
 * | Path                                   | Base            | Credentials | Bearer | CSRF        | Refreshable |
 * |----------------------------------------|-----------------|-------------|--------|-------------|-------------|
 * | `/api/auth/csrf`                       | authOrigin      | yes         | no     | no          | no          |
 * | `/api/auth/refresh`, `/api/auth/logout`| authOrigin      | yes         | no     | yes         | no          |
 * | `/api/account/**`                      | authOrigin      | yes         | yes    | writes only | yes         |
 * | `/api/reseller-admin/**`               | authOrigin      | no          | yes    | no          | yes         |
 * | `/v1/reseller-admin/**` (tmm-api)      | `env.apiBaseUrl`| no          | yes    | no          | yes         |
 *
 * `authOrigin` is read verbatim from the resolved bootstrap; `env.apiBaseUrl` is the
 * same-origin `/api` prefix (rewritten onto tmm-api at the edge). CSRF is **`store-token`**:
 * saas-auth's CSRF cookie is unreadable from this origin, so the token comes from
 * `GET /api/auth/csrf` and every refresh response, held in memory (`authStore`), and sent as
 * `X-CSRF-Token` only (no body echo).
 *
 * These are the only surfaces: saas-auth's tenant-console routes and tmm-api's tenant-console
 * prefix are not recognized (TMMRSL-0029/0030) — a tenant's people and custom domain are
 * `/api/reseller-admin/tenants/{tenantId}/…`, its tmm-api screens `/v1/reseller-admin/tenants/{slug}/…`.
 *
 * `404 TENANT_NOT_FOUND` on a per-tenant tmm-api call is reported to `tenantGone.ts`, so the open
 * tenant workspace leaves whichever caller saw it (H §4.5).
 *
 * Repo-side config on the factory's instance (never inside it):
 *  - **Refresh = csrf → refresh.** Every `POST /api/auth/refresh` is preceded by a fresh
 *    `GET /api/auth/csrf` (H §4.2). That is also the `403 INVALID_CSRF` recovery: the factory
 *    answers a stale-token 403 on a store-token write by refreshing, and the refresh now
 *    re-fetches the CSRF token first, so the single retry carries a good one.
 *  - **One refresh in flight.** The refresh POST is single-flighted at the transport, so the
 *    factory's reactive 401 path and the proactive timer (`silentRefresh.ts`) share one call.
 *  - **Session-ending codes.** `RESELLER_ACCESS_REVOKED` (401 on refresh, 403 on tmm-api) and
 *    `REFRESH_ORIGIN_UNROUTABLE` / `INVALID_ORIGIN` end the session with a reason
 *    (`revoked` / `misconfigured`) instead of the generic expiry (H §4.5).
 *  - **`Retry-After`.** A 429's `Retry-After` is carried on the thrown `ApiError` as
 *    `details.retryAfterSeconds` (H §4.5).
 *
 * Every call carries `X-Request-ID`. A `401` on a bearer call triggers ONE silent refresh and
 * ONE retry — bounded, never looping (the factory's `_retry` guard guarantees it).
 */
import axios, {
  type AxiosAdapter,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponseTransformer,
  type InternalAxiosRequestConfig,
} from 'axios';
import { createApiClient, ApiError as FactoryApiError } from './httpClientFactory';
import { ApiError, statusToCode } from './errors';
import { reportTenantGone, tenantSlugOfPath } from './tenantGone';
import { generateRequestId } from '@/utils/uuid';
import { env } from '@/config/env';
import { getBootstrap } from '@/config/bootstrap';
import {
  getAccessToken,
  getCsrfToken,
  getSessionEpoch,
  setAccessTokenIfCurrent,
  setCsrfTokenIfCurrent,
  signOut,
  type SessionEndReason,
} from '@/auth/authStore';

const REFRESH_PATH = '/api/auth/refresh';
const CSRF_PATH = '/api/auth/csrf';

/** saas-auth session surface — csrf / refresh / logout (cookies, never a bearer). */
export function isAuthSurface(path: string): boolean {
  return path.startsWith('/api/auth/');
}

/** saas-auth own-account surface (`/api/account/**`) — cookies AND bearer, CSRF on writes. */
export function isAccountSurface(path: string): boolean {
  return path.startsWith('/api/account/');
}

/** saas-auth bearer-only surface: `/api/reseller-admin/**` (reseller-wide and per tenant, by id). */
export function isAuthBearerSurface(path: string): boolean {
  return path.startsWith('/api/reseller-admin/');
}

/** tmm-api bearer-only surface: `/v1/reseller-admin/**`. */
export function isBearerSurface(path: string): boolean {
  return path.startsWith('/v1/reseller-admin/');
}

/** Every path that carries the bearer — and is refreshable on a 401. */
export function isProtectedBearerSurface(path: string): boolean {
  return isBearerSurface(path) || isAuthBearerSurface(path) || isAccountSurface(path);
}

/** Cookies travel ONLY to the session surface and `/api/account/**`. */
function sendsCredentials(path: string): boolean {
  return isAuthSurface(path) || isAccountSurface(path);
}

/**
 * Two-base routing: tmm-api → `env.apiBaseUrl` (the one computed origin); everything else →
 * saas-auth, whose origin is read verbatim from the resolved bootstrap. `main.tsx`'s
 * `BootstrapGate` guarantees bootstrap has resolved before any request can be made.
 */
function baseUrlFor(path: string): string {
  if (isBearerSurface(path)) return env.apiBaseUrl;
  return getBootstrap().authOrigin;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** The reason a saas-auth / tmm-api error code ends the session, if it does. */
function endReasonOf(code: unknown): SessionEndReason | null {
  if (code === 'RESELLER_ACCESS_REVOKED') return 'revoked';
  if (code === 'REFRESH_ORIGIN_UNROUTABLE' || code === 'INVALID_ORIGIN') return 'misconfigured';
  return null;
}

/**
 * A session-ending code seen on the factory's INTERNAL refresh call. The factory swallows that
 * error and calls `onSessionExpired`; this is how the reason reaches it.
 */
let pendingEndReason: SessionEndReason | null = null;

function takePendingEndReason(): SessionEndReason | null {
  const reason = pendingEndReason;
  pendingEndReason = null;
  return reason;
}

const instance: AxiosInstance = createApiClient({
  resolveBaseUrl: baseUrlFor,
  sendCredentials: sendsCredentials,
  tokens: {
    getAccess: getAccessToken,
    // The factory forwards the refresh body's `expiresAt`, which saas-auth does not send (it
    // sends `expiresIn`); the store then takes the expiry from the token's `exp` claim.
    setAccess: (token, expiresAt) =>
      setAccessTokenIfCurrent(
        refreshFlightEpoch,
        token,
        typeof expiresAt === 'string' ? expiresAt : null,
      ),
    getCsrf: getCsrfToken,
    // Replace the stored token only when the response carries one (H §4.2).
    setCsrf: (token) => {
      if (token) setCsrfTokenIfCurrent(refreshFlightEpoch, token);
    },
  },
  bearer: { protected: isProtectedBearerSurface },
  csrf: {
    protected: (path, method) =>
      MUTATING.has(method) && (isAuthSurface(path) || isAccountSurface(path)),
    mode: () => 'store-token',
  },
  refresh: { path: REFRESH_PATH },
  onSessionExpired: () => signOut({ reason: takePendingEndReason() ?? 'expired' }),
  generateRequestId,
});

/** Path of an axios request config — bare path or the pathname of an absolute URL. */
function pathOf(req: { url?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}

function isRefreshCall(req: { url?: string; method?: string }): boolean {
  return pathOf(req) === REFRESH_PATH && (req.method ?? 'get').toUpperCase() === 'POST';
}

/** Unwrap saas-auth's `{ success, data }` envelope when present. */
function unwrapEnvelope(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body && 'success' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

/**
 * `GET /api/auth/csrf` (credentials) → store the token. A failure leaves the old one.
 * Also run before `POST /api/auth/logout` (`auth.ts`) so a token another tab rotated is never sent.
 */
export async function primeCsrf(): Promise<void> {
  const epoch = getSessionEpoch();
  try {
    const res = await instance.get<unknown>(CSRF_PATH, { _retry: true } as AxiosRequestConfig & {
      _retry: boolean;
    });
    const body = unwrapEnvelope(res.data) as { csrfToken?: unknown } | null;
    if (typeof body?.csrfToken === 'string' && body.csrfToken.length > 0) {
      setCsrfTokenIfCurrent(epoch, body.csrfToken);
    }
  } catch {
    /* the refresh answers for itself */
  }
}

let refreshFlight: ReturnType<AxiosAdapter> | null = null;
/** Epoch captured when the current refresh flight started — not cleared in `finally`. */
let refreshFlightEpoch = 0;

/**
 * Request interceptors run last-registered-first, so these run BEFORE the factory's:
 *  1. Pre-mark `/api/auth/**` as `_retry` — the session surface either succeeds or is terminally
 *     unauthenticated; a refresh must never recurse into another refresh (ported from dink-admin
 *     DNKADM-0037). The factory's `!_retry` gate then skips it.
 *
 * The CSRF prime for a refresh happens inside the refresh flight (the adapter below), so
 * concurrent refreshes fetch ONE token and the shared POST carries it.
 */
instance.interceptors.request.use((req) => {
  const r = req as InternalAxiosRequestConfig & { _retry?: boolean };
  if (isAuthSurface(pathOf(req)) && r._retry === undefined) {
    r._retry = true;
  }
  return req;
});

/**
 * Record a session-ending code on the way out. Response interceptors run in registration order,
 * so this sees the factory's normalized error — including the one from its own refresh call.
 */
instance.interceptors.response.use(undefined, (err: unknown) => {
  if (err instanceof FactoryApiError) {
    const reason = endReasonOf(err.code);
    if (reason) pendingEndReason = reason;
    // A host misconfiguration is an operator problem; leave the code where support can see it.
    if (reason === 'misconfigured') console.error(`saas-auth session error: ${err.code}`);
  }
  return Promise.reject(err);
});

/**
 * Single-flight the refresh at the transport: the first caller runs csrf → refresh (the fresh
 * token replaces the header the factory set from the store); concurrent callers join it and share
 * one response.
 */
const baseAdapter = axios.getAdapter(instance.defaults.adapter);
instance.defaults.adapter = (config) => {
  if (!isRefreshCall(config)) return baseAdapter(config);
  if (refreshFlight === null) {
    refreshFlightEpoch = getSessionEpoch();
    refreshFlight = primeCsrf()
      .then(() => {
        const token = getCsrfToken();
        if (token) config.headers.set('X-CSRF-Token', token);
        return baseAdapter(config);
      })
      .finally(() => {
        refreshFlight = null;
      });
  }
  return refreshFlight.then((res) => ({ ...res, config }));
};

/** `Retry-After` of a 429, by request id, until the wrapper reads it. */
const retryAfterByRequest = new Map<string, number>();

const captureRetryAfter: AxiosResponseTransformer = function (data, headers, status) {
  if (status === 429) {
    const seconds = parseRetryAfter(headers.get?.('retry-after'));
    const id = this.headers?.get?.('X-Request-ID');
    if (seconds !== null && typeof id === 'string') retryAfterByRequest.set(id, seconds);
  }
  return data;
};
const defaultTransforms = instance.defaults.transformResponse;
instance.defaults.transformResponse = [
  captureRetryAfter,
  ...(Array.isArray(defaultTransforms)
    ? defaultTransforms
    : defaultTransforms
      ? [defaultTransforms]
      : []),
];

/** Seconds from a `Retry-After` value — delta-seconds or an HTTP date. */
function parseRetryAfter(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (!Number.isFinite(at)) return null;
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

/** Test seam — clears the pending session-end reason and any unread `Retry-After`. The factory's
 *  own refresh latch self-clears. */
export function __resetClientState(): void {
  pendingEndReason = null;
  retryAfterByRequest.clear();
}

export interface RequestOptions {
  json?: unknown;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  // The factory emits Accept + X-Request-ID; this wrapper adds caller headers and the
  // conditional Content-Type (only with a JSON body).
  const headers: Record<string, string> = { ...options.headers };
  if (options.json !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const config: AxiosRequestConfig = {
    method,
    // Pass the bare path as the axios `url`; the factory's request interceptor sets `baseURL`
    // from `resolveBaseUrl(path)` so the surface predicates and two-base routing operate on
    // the clean path (not a query-laden URL).
    url: path,
    headers,
  };
  if (options.json !== undefined) {
    config.data = options.json;
  }
  if (options.query) {
    config.params = options.query;
  }

  let data: T;
  try {
    const res = await instance.request<T>(config);
    data = res.status === 204 ? (undefined as T) : res.data;
  } catch (err) {
    const mapped = toApiError(err);
    const factoryExpired = err instanceof FactoryApiError && err.code === 'SESSION_EXPIRED';
    // A session-ending code on THIS call (the boot refresh, or tmm-api's live member check)
    // ends the session with its reason. On the factory's refresh path `onSessionExpired`
    // already did, and this call sees SESSION_EXPIRED instead.
    const reason = factoryExpired ? null : endReasonOf(mapped.details?.code);
    const goneSlug =
      mapped.details?.code === 'TENANT_NOT_FOUND' && isBearerSurface(path)
        ? tenantSlugOfPath(path)
        : null;
    if (goneSlug !== null) reportTenantGone(goneSlug);
    if (reason) {
      takePendingEndReason();
      signOut({ reason });
    } else if (
      // Persistent 401 after a successful refresh: the factory does not call onSessionExpired
      // (that fires only when the refresh POST itself fails). Expire the session here so
      // AuthGate shows the interstitial. Skip the session surface (a logout 401 must not
      // expire-via-this-path).
      isProtectedBearerSurface(path) &&
      mapped.code === 'SESSION_EXPIRED' &&
      !factoryExpired
    ) {
      signOut({ reason: 'expired' });
    }
    throw mapped;
  }

  // Normalize an empty body to `null`; 204 already returned above.
  return data === '' ? (null as T) : data;
}

/**
 * Convert a thrown factory `ApiError` into tmm-reseller-admin's `ApiError`: `NETWORK_ERROR` on
 * transport failure (status 0), `SESSION_EXPIRED` on a dead session, otherwise
 * `statusToCode(status)` with the backend envelope code preserved under `details.code` and its
 * message under `details.message`.
 *
 * tmm-api answers every error with `{ success:false, error:{ code, message, retryable,
 * requestId, details? } }`; saas-auth with `{ success:false, error:{ code, message } }`. The
 * factory's normalizer reads `error.code` / `error.message` from either; `error.details` is
 * `details.backend` (field-error map `details.backend.fields`). `error.retryable` is not carried
 * — do not fork the factory to get it early.
 */
function toApiError(err: unknown): ApiError {
  if (err instanceof FactoryApiError) {
    if (err.code === 'NETWORK_ERROR') {
      return new ApiError('NETWORK_ERROR', 0, initFrom(err.requestId));
    }
    if (err.code === 'SESSION_EXPIRED') {
      return new ApiError('SESSION_EXPIRED', 401, initFrom(err.requestId));
    }
    return apiErrorFromFactory(err);
  }
  if (err instanceof ApiError) return err;
  return new ApiError('UNKNOWN', 0, {});
}

/**
 * Build tmm-reseller-admin's `ApiError` from a normalized factory error. This is an ALLOW-LIST,
 * not a passthrough (ported lesson, dink-admin DNKADM-0393): only the keys named here reach
 * `details`. Message precedence: the factory's `details.message` (the envelope's
 * `error.message`), else a raw `{ message }`, else a raw `{ error: { message } }`, else a
 * non-empty raw string.
 */
function apiErrorFromFactory(err: FactoryApiError): ApiError {
  const status = err.status;
  const raw = err.details.raw;
  let message: string | undefined = err.details.message;
  if (message === undefined && raw && typeof raw === 'object') {
    const b = raw as { message?: unknown; error?: unknown };
    if (typeof b.message === 'string') message = b.message;
    else if (
      b.error &&
      typeof b.error === 'object' &&
      typeof (b.error as { message?: unknown }).message === 'string'
    ) {
      message = (b.error as { message: string }).message;
    }
  } else if (message === undefined && typeof raw === 'string' && raw.length > 0) {
    message = raw;
  }
  const envelopeCode = err.code !== 'UNKNOWN' ? err.code : undefined;
  let details: Record<string, unknown> | undefined;
  if (message !== undefined || envelopeCode !== undefined) {
    details = {};
    if (message !== undefined) details['message'] = message;
    if (envelopeCode !== undefined) details['code'] = envelopeCode;
  }
  if (err.details.backend !== undefined) {
    details = details ?? {};
    details['backend'] = err.details.backend;
  }
  if (err.requestId !== undefined) {
    const retryAfter = retryAfterByRequest.get(err.requestId);
    if (retryAfter !== undefined) {
      retryAfterByRequest.delete(err.requestId);
      details = details ?? {};
      details['retryAfterSeconds'] = retryAfter;
    }
  }
  return new ApiError(statusToCode(status), status, initFrom(err.requestId, details));
}

/** Build an `ApiErrorInit` without `undefined` properties (`exactOptionalPropertyTypes`). */
function initFrom(
  requestId: string | undefined,
  details?: Record<string, unknown>,
): { requestId?: string; details?: Record<string, unknown> } {
  const init: { requestId?: string; details?: Record<string, unknown> } = {};
  if (requestId !== undefined) init.requestId = requestId;
  if (details !== undefined) init.details = details;
  return init;
}
